// Copyright (c) Docugami, Inc. All rights reserved.

import { createEntityAdapter, createSlice, EntityState, PayloadAction } from "@reduxjs/toolkit";
import { createApi } from "@reduxjs/toolkit/dist/query/react";
import { Config } from "config";
import { AddExportConfigurationRequest, BaseExportConfigurationRequest, CloneWorkPathEntriesRequest, CloneWorkPathEntriesResponse, CreateWorkPathRequest, ExportConfiguration, ExportConfigurationStatus, SaveWorkPathResponse, UpdateExportConfigurationRequest, WorkPath, WorkPathEvaluationMessage } from "networking/WorkPathApi";
import { Dispatch } from "react";
import { AppState } from "store";
import { createDgBaseQuery } from "store/baseQuery";
import { TelemetryExceptions } from "telemetry/TelemetryEvents";
import { Telemetry } from "telemetry/TelemetryManager";

export const workPathsAdapter = createEntityAdapter<WorkPath>({
    selectId: x => x.id
});

export const {
    selectAll: selectAllWorkPaths
} = workPathsAdapter.getSelectors();

type WithWorkPathId<T> = T & { workPathId: string };

const workPathBaseQuery = createDgBaseQuery(Config.reportsApiUrl.replace(/\/+$/, ""));
export const workPathsApi = createApi({
    reducerPath: 'workPathApi',
    tagTypes: ['workPaths', 'exportConfigurations'],
    baseQuery: workPathBaseQuery,
    endpoints: builder => ({
        getWorkPaths: builder.query<EntityState<WorkPath>, void>({
            query: () => `/workpaths`,
            transformResponse: (response: WorkPath[], _, request) => {
                const workPaths: WorkPath[] = [];
                response.forEach(model => {
                    if (model.version !== '1.0') {
                        workPaths.push(model);
                    }
                });

                return workPathsAdapter.setAll(workPathsAdapter.getInitialState(), workPaths);
            },
            providesTags: ['workPaths']
        }),
        deleteWorkPaths: builder.mutation<boolean, string[]>({
            query: workPathIds => ({
                url: `/workpaths`,
                method: 'DELETE',
                body: workPathIds
            }),
            onQueryStarted: async (ids, api) => {
                await api.queryFulfilled;
                api.dispatch(workPathsApi.util.invalidateTags(['workPaths']));
            }
        }),
        createWorkPath: builder.mutation<SaveWorkPathResponse, CreateWorkPathRequest>({
            query: request => {
                return {
                    url: `/workpaths`,
                    method: 'POST',
                    body: request
                }
            },
            onQueryStarted: async (arg, api) => {
                try {
                    await api.queryFulfilled;
                    api.dispatch(workPathsApi.util.invalidateTags(['workPaths']));
                }
                catch (error) {
                    Telemetry.trackExceptionEvent(TelemetryExceptions.WorkPathConstructionFailed, { error: error });
                }
            }
        }),
        renameWorkPath: builder.mutation<void, WithWorkPathId<{name: string}>>({
            query: request => ({
                url: `/workpaths/${encodeURIComponent(request.workPathId)}/name`,
                method: 'PUT',
                body: request.name
            }),
            onQueryStarted: async (arg, api) => {
                await api.queryFulfilled;
                api.dispatch(workPathsApi.util.invalidateTags(['workPaths']));
            }
        }),
        cloneWorkPaths: builder.mutation<WorkPath, CloneWorkPathEntriesRequest>({
            query: request => ({
                url: `/workpaths/clone`,
                method: 'POST',
                body: request
            }),
            onQueryStarted: async (arg, api) => {
                try {
                    await api.queryFulfilled;
                    api.dispatch(workPathsApi.util.invalidateTags(['workPaths']));
                }
                catch (error) {
                    Telemetry.trackExceptionEvent(TelemetryExceptions.WorkPathConstructionFailed, { error: error });
                }
            },
            transformResponse: (response: CloneWorkPathEntriesResponse) => response.workPath
        }),
        getExportConfiguation: builder.query<ExportConfiguration | undefined, string>({
            query: workPathId => `/workpaths/${workPathId}/export`,
            providesTags: (_, __, arg) => [{ type: 'exportConfigurations', id: arg }]
        }),
        addExportConfiguration: builder.mutation<void, WithWorkPathId<AddExportConfigurationRequest>>({
            query: request => ({
                url: `/workpaths/${request.workPathId}/export`,
                method: 'POST',
                body: request
            }),
            onQueryStarted: async (arg, api) => {
                await api.queryFulfilled;
                api.dispatch(workPathsApi.util.invalidateTags([{ type: 'exportConfigurations', id: arg.workPathId }]));
            }
        }),
        updateExportConfigurationStatus: builder.mutation<void, WithWorkPathId<UpdateExportConfigurationRequest>>({
            query: request => ({
                url: `/workpaths/${request.workPathId}/export/${request.configurationId}/status`,
                method: 'POST',
                body: request.disabled
            }),
            onQueryStarted: async (arg, api) => {
                await api.queryFulfilled;
                api.dispatch(workPathsApi.util.invalidateTags([{ type: 'exportConfigurations', id: arg.workPathId }]));
            }
        }),
        deleteExportConfiguration: builder.mutation<void, WithWorkPathId<BaseExportConfigurationRequest>>({
            query: request => ({
                url: `/workpaths/${request.workPathId}/export/${request.configurationId}`,
                method: 'DELETE',
                body: request
            }),
            onQueryStarted: async (arg, api) => {
                await api.queryFulfilled;
                api.dispatch(workPathsApi.util.invalidateTags([{ type: 'exportConfigurations', id: arg.workPathId }]));
            }
        }),
        isEvaluatingConfiguration: builder.query<boolean, WithWorkPathId<BaseExportConfigurationRequest>>({
            query: request => `/workpaths/${request.workPathId}/export/${request.configurationId}/evaluate`
        }),
    })
});

export const {
    useGetWorkPathsQuery,
    useDeleteWorkPathsMutation,
    useRenameWorkPathMutation,
    useCreateWorkPathMutation,
    useCloneWorkPathsMutation,
    useGetExportConfiguationQuery,
    useAddExportConfigurationMutation,
    useUpdateExportConfigurationStatusMutation,
    useDeleteExportConfigurationMutation,
    useIsEvaluatingConfigurationQuery
} = workPathsApi

export const selectGetWorkPaths = (state: AppState) => workPathsApi.endpoints.getWorkPaths.select()(state).data;

interface WorkPathsSliceState {
    currentWorkPath?: WorkPath
}

export const workPathsSlice = createSlice({
    name: 'workPaths',
    initialState: {
    } as WorkPathsSliceState,
    reducers: {
        setCurrentWorkPath(state, action: PayloadAction<WorkPath | undefined>) {
            state.currentWorkPath = !!action.payload ? {
                ...action.payload,
                resources: {...action.payload.resources},
            } : undefined;
        }
    }
});

export const { setCurrentWorkPath } = workPathsSlice.actions;

export function handleWorkPathExportInitialization(message: WorkPathEvaluationMessage): any {
    return function (dispatch: Dispatch<any>, getState: (() => AppState)) {
        if (message.succeeded === undefined) {
            return;
        }

        dispatch(workPathsApi.util.updateQueryData('getExportConfiguation', message.workPathId, config => {
            if (!!config) {
                config.status = message.succeeded ? ExportConfigurationStatus.Ready : ExportConfigurationStatus.Uninitialized;
            }
        }));
    }
}